
import { FC, useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import Select from "react-select";
import { format, parse, } from "date-fns";
import DatePicker from "react-datepicker";
import ReactInputMask from "react-input-mask";
import { useDebouncedCallback } from "use-debounce";


import { reportFilterProps, ReportFilterState } from "./types";
import { limitShift, offset, shift } from "@floating-ui/react";
import useFetch from "src/hooks/useFetch";
import { APIEndpoints } from "src/types/apiTypes";
import { useParams } from "react-router-dom";

type ListingOption = {
    value: string;
    label: string;
};

type ListingItem = {
    id: number;
    name: string;
};

const toListingOption = (item: ListingItem): ListingOption => ({
    value: item.id.toString(),
    label: item.name,
});

const normalizeReportName = (reportName?: string): string => {
    if (!reportName) return "";

    const normalizedName = reportName.startsWith("pre_")
        ? reportName.replace(/^pre_/, "")
        : reportName;

    return normalizedName.endsWith("_report")
        ? normalizedName
        : `${normalizedName}_report`;
};



const ReportsFilter: FC<reportFilterProps> = ({ updateQueryParams, defaultQueryParams }): JSX.Element => {

    const [filteredDocListing, updatedFilteredDocListing] = useState<ListingItem[]>([])
    const [filteredPracticeListing, updatedFilteredPracticeListing] = useState<ListingItem[]>([])
    const [filteredInsuranceListing, updatedFilteredInsuranceListing] = useState<ListingItem[]>([])
    const { reportType } = useParams<{ reportType: string }>();

    const { control, handleSubmit, reset, watch } = useForm<ReportFilterState>({
        mode: 'all',
        defaultValues: defaultQueryParams,
    });
    const start_date = watch("start_date");
    const end_date = watch("end_date");
    const reportName = normalizeReportName(defaultQueryParams?.report_name || reportType);

    const showPractice =
        reportName === "refill_report" ||
        reportName === "full_report" ||
        reportName === "field_liaison_report";


    const showDoctor =

        reportName === "status_report" ||
        reportName === "condensed_sales_report" ||
        reportName === "refill_report" ||
        reportName === "full_report" ||
        reportName === "field_liaison_report";

    const showInsurance =
        reportName === "status_report" ||
        reportName === "condensed_sales_report" ||
        reportName === "refill_report" ||
        reportName === "full_report";


    const showPracticeCity =
        reportName === "condensed_sales_report";

    const showHcpAddress =
        reportName === "status_report";

    const showCommunicationMethod =
        reportName === "field_liaison_report";

    const COMMUNICATION_METHOD_OPTIONS = [
        { value: "Phone", label: "Phone" },
        { value: "Fax", label: "Fax" },
        { value: "Email", label: "Email" },
        { value: "Portal", label: "Portal" },
    ];

    const { error: insuranceListingApiError, data: insuranceListingApiResponse, mutate: getinsuranceListing } =
        useFetch<{ id: number; name: string; }[]>(APIEndpoints.FETCH_INSURANCE);

    /** @first */
    useEffect(() => {
        if (insuranceListingApiResponse || insuranceListingApiError) {
            if (insuranceListingApiResponse) {
                updatedFilteredInsuranceListing((oldVal) => {
                    const merged = [...oldVal, ...insuranceListingApiResponse];
                    const uniqueSorted = Array.from(new Map(merged.map(i => [i.id, i])).values())
                        .sort((a, b) => (a.name > b.name ? 1 : -1));
                    return uniqueSorted;
                });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [insuranceListingApiResponse, insuranceListingApiError]);


    const { error: doctorListingApiError, data: doctorListingApiResponse, mutate: getdoctorListing } =
        useFetch<{ id: number; name: string; }[]>(APIEndpoints.GET_MANUFACTURER_FILTER_DOCTOR);



    // /** @second */
    // useEffect(() => {
    //     if (doctorListingApiResponse && !doctorListingApiError) {
    //       updatedFilteredDocListing((prev) => {
    //         const combined = [...prev, ...doctorListingApiResponse];
    //         // Deduplicate by ID
    //         const unique = Array.from(new Map(combined.map((doc) => [doc.id, doc])).values());
    //         // Sort alphabetically
    //         return unique.sort((a, b) => a.name.localeCompare(b.name));
    //       });
    //     }
    //     // eslint-disable-next-line react-hooks/exhaustive-deps
    //   }, [doctorListingApiResponse]);


    useEffect(() => {
        if (doctorListingApiResponse && !doctorListingApiError) {
            const unique = Array.from(
                new Map(doctorListingApiResponse.map((doc) => [doc.id, doc])).values()
            );

            updatedFilteredDocListing(
                unique.sort((a, b) => a.name.localeCompare(b.name))
            );
            // if (doctorListingApiResponse) {
            //     updatedFilteredDocListing((oldVal) => {
            //         const newDoctors = [...oldVal, ...doctorListingApiResponse]
            //             .sort((doctorA, doctorB) => (doctorA.name > doctorB.name) ? 1 : -1)
            //         return [...new Map(newDoctors.map(doctor => [doctor["id"], doctor])).values()];
            //     })
            // }
        }
    }, [doctorListingApiResponse, doctorListingApiError]);




    const { error: practiceListingApiError, data: practiceListingApiResponse, mutate: getpracticeListing } =
        useFetch<{ id: number; name: string; }[]>(APIEndpoints.GET_MANUFACTURER_FILTER_PRACTICE);



    /** @third */
    useEffect(() => {
        if (practiceListingApiResponse || practiceListingApiError) {
            if (practiceListingApiResponse) {
                updatedFilteredPracticeListing((oldVal) => {
                    const newPatients = [...oldVal, ...practiceListingApiResponse]
                        .sort((patientA, patientB) => (patientA.name > patientB.name) ? 1 : -1)
                    return [...new Map(newPatients.map(patient => [patient["id"], patient])).values()];
                })
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [practiceListingApiResponse, practiceListingApiError]);




    const searchForStats = (values: ReportFilterState) => {
        const updatedValues = {
            ...defaultQueryParams,
            ...values,
            practice_id:
                values.practice_id ?
                    (typeof values.practice_id === "string" ? values.practice_id : (values.practice_id as unknown as { value: string; label: string; }).value)
                    : "",
            drug_name:
                values.drug_name
                    ? (typeof values.drug_name === "string"
                        ? values.drug_name === "All Products"
                            ? ""
                            : values.drug_name
                        : (values.drug_name as { value: string; label: string }).value === "All Products"
                            ? ""
                            : (values.drug_name as { value: string; label: string }).value)
                    : "",
            global_doctor_ids: typeof values.global_doctor_ids === "string"
                ? values.global_doctor_ids
                : Array.isArray(values.global_doctor_ids)
                    ? (values.global_doctor_ids as any[]).join(",")
                    : String((values.global_doctor_ids as any)?.value ?? ""),
            insurance_name:
                values.insurance_name ?
                    (typeof values.insurance_name === "string" ? values.insurance_name : (values.insurance_name as unknown as { value: string; label: string; }).label)
                    : "",
            communication_method:
                values.communication_method ?
                    (typeof values.communication_method === "string" ? values.communication_method : (values.communication_method as unknown as { value: string; label: string; }).label)
                    : "",
            practice_city:
                values.practice_city ?
                    (typeof values.practice_city === "string" ? values.practice_city : (values.practice_city as unknown as { value: string; label: string; }).label)
                    : "",
            hcp_address:
                values.hcp_address ?
                    (typeof values.hcp_address === "string" ? values.hcp_address : (values.hcp_address as unknown as { value: string; label: string; }).label)
                    : "",

            // insurance_name:
            //     values.insurance_name ?
            //         (typeof values.insurance_name === "string" ? values.insurance_name : (values.insurance_name as unknown as { value: string; label: string; }).label)
            //         : "",
        };

        updateQueryParams(updatedValues, false);
    };






    //:- Filtering practice from api recieved listing
    const filterPractice = (search: string) => {
        if (search.length >= 2) {
            getpracticeListing({}, {}, { query: search.trim() })
            // updatedFilteredPracticeListing(
            //     practiceDropdown.filter((prac) => prac.name.toLowerCase().includes(search.toLowerCase()))
            // )
        }
    };
    const debouncedFilterPractice = useDebouncedCallback(filterPractice, 500);

    //:- Filtereing doctor from api recieved listing
    // const filterDoctor = (search: string) => {
    //     console.log("FILTER DOCTOR CALLED WITH SEARCH 👉", search);
    //     if (search.length >= 2) {
    //         getdoctorListing({}, {}, { query: search.trim() })
    //     }

    // };
    const filterDoctor = (search: string) => {
        const cleaned = search.trim();

        if (cleaned.length < 2) {
            updatedFilteredDocListing([]);
            return
        }

        // ✅ make it flexible: "shawn milburn" -> "shawn%milburn"
        const wildcardQuery = cleaned.replace(/\s+/g, "%");

        getdoctorListing({}, {}, { query: wildcardQuery });
    };
    const debouncedFilterDoctor = useDebouncedCallback(filterDoctor, 500);

    const filterInsurance = (search: string) => {
        if (search.length >= 2) {
            getinsuranceListing({}, {}, { insurance_name: search.trim() })
        }

    };
    const debouncedFilterInsurance = useDebouncedCallback(filterInsurance, 500);



    const handlePracticeChange = (practiceId: string) => {
        practiceId && getdoctorListing({}, {},
            { practice_id: practiceId }
        )
    };

    const clearFormData = () => {
        const values: ReportFilterState = {
            ...defaultQueryParams,
            start_date: format((new Date()).setDate((new Date()).getDate() - 30), "yyyy-MM-dd"),
            end_date: format((new Date()).setDate((new Date()).getDate() + 1), "yyyy-MM-dd"),
            practice_id: "",
            global_doctor_ids: "",
            page: 1,
            pagination: true,
            report_name: "",
            file_required: false,
            drug_name: "",
            insurance_name: "",
            communication_method: "",
            practice_city: "",
            hcp_address: ""
        };

        reset(values);
        updatedFilteredDocListing([]);
        updatedFilteredPracticeListing([]);
        updatedFilteredInsuranceListing([]);
        updateQueryParams(values, true);
    };

    //     useEffect(() => {
    //   reset(defaultQueryParams);
    // }, [defaultQueryParams]);

    return (
        <form className="statistics-filter row g-2" onSubmit={handleSubmit(searchForStats)}>
            {/* <div className="col product-field mb-2">
                <Controller
                    name="drug_name"
                    control={control}
                    defaultValue={DRUG_NAME[0]?.value} // pehla option default
                    render={({ field }) => {
                        //console.log("drug_name field.value 👉", field.value);
                        return (
                            <Select
                                id="drug_name"
                                placeholder="Select Product"
                                options={
                                    DRUG_NAME.length > 0
                                        ? DRUG_NAME.map((item) => ({
                                            value: item.value,
                                            label: item.label,
                                        }))
                                        : []
                                }
                                value={
                                    field.value
                                        ? DRUG_NAME.find((item) => item.value === field.value)
                                        : null
                                }
                                onChange={(selectedOption) =>
                                    field.onChange(selectedOption ? selectedOption.value : "")
                                }
                                onBlur={field.onBlur}
                            />
                        );
                    }}
                />


            </div> */}
            <div className="col date-col mb-2">
                <Controller
                    name="start_date"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                        <DatePicker
                            selected={field.value.length > 0 ? parse(field.value, "yyyy-MM-dd", new Date()) : null}
                            onChange={(date) => date ? field.onChange(format(date, "yyyy-MM-dd")) : ""}
                            customInput={
                                <ReactInputMask mask="99/99/9999">{(inputProps: {}) =>
                                    <input {...inputProps} />}
                                </ReactInputMask>
                            }
                            className="form-control" wrapperClassName="w-100"
                            placeholderText="mm/dd/yyyy"
                            dateFormat="MM/dd/yyyy"
                            showMonthDropdown
                            showYearDropdown
                            maxDate={end_date ? new Date(end_date) : undefined}
                            portalId="root"
                            popperModifiers={[offset(8), shift({ limiter: limitShift() })]}
                        />
                    )}
                />
            </div>
            <div className="col date-col mb-2">
                <Controller
                    name="end_date"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                        <DatePicker
                            selected={field.value.length > 0 ? parse(field.value, "yyyy-MM-dd", new Date()) : null}
                            onChange={(date) => date ? field.onChange(format(date, "yyyy-MM-dd")) : ""}
                            customInput={<ReactInputMask mask="99/99/9999">{(inputProps: {}) => <input {...inputProps} />}</ReactInputMask>}
                            className="form-control" wrapperClassName="w-100"
                            placeholderText="mm/dd/yyyy"
                            dateFormat="MM/dd/yyyy"
                            showMonthDropdown
                            showYearDropdown
                            minDate={start_date ? new Date(start_date) : undefined}
                            portalId="root"
                            popperModifiers={[offset(8), shift({ limiter: limitShift() })]}
                        />
                    )}
                />
            </div>
            {showPractice && (
                <div className="col min-col mb-2">
                    <Controller
                        name="practice_id"
                        control={control}
                        render={({ field }) => {
                            const practiceOptions = filteredPracticeListing.map(toListingOption);

                            return (
                                <Select<ListingOption, false>
                                    name={field.name}
                                    onBlur={field.onBlur}
                                    options={practiceOptions}
                                    id="practice_id"
                                    placeholder="Select Practice"
                                    isSearchable={true}
                                    isClearable={true}
                                    value={
                                        field.value
                                            ? practiceOptions.find((opt) => opt.value === field.value) ?? null
                                            : null
                                    }
                                    onChange={(selected) => {
                                        const value = selected?.value || "";
                                        field.onChange(value);
                                        handlePracticeChange(value);
                                    }}
                                    noOptionsMessage={() => "Please enter 2 or more characters to search..."}
                                    onInputChange={debouncedFilterPractice}
                                //onMenuClose={() => updatedFilteredPracticeListing([])}

                                />
                            )
                        }}
                    />
                </div>)}
            {showDoctor && (
                <div className="col min-col mb-2">
                    <Controller
                        name="global_doctor_ids"
                        control={control}
                        defaultValue={''}
                        render={({ field }) => {
                            const doctorOptions = filteredDocListing.map(toListingOption);

                            return (
                                <Select<ListingOption, false>
                                    name={field.name}
                                    onBlur={field.onBlur}
                                    options={doctorOptions}
                                    id="global_doctor_ids"
                                    placeholder="Select Doctor"
                                    isSearchable={true}
                                    isClearable={true}
                                    value={
                                        field.value
                                            ? doctorOptions.find((opt) => opt.value === field.value) ?? null
                                            : null
                                    }
                                    onChange={(selected) => {
                                        field.onChange(selected?.value || "");
                                    }}
                                    filterOption={(option, inputValue) => {
                                        const label = option.label.toLowerCase();
                                        const tokens = inputValue.toLowerCase().trim().split(/\s+/).filter(Boolean);

                                        return tokens.every(t => label.includes(t));
                                    }}
                                    noOptionsMessage={() => "Please enter 2 or more characters to search..."}
                                    onInputChange={(inputValue, actionMeta) => {
                                        if (actionMeta.action === "input-change") {
                                            debouncedFilterDoctor(inputValue);
                                        }
                                    }}
                                />
                            )
                        }}
                    />


                </div>)}
            {showInsurance && (
                <div className="col min-col mb-2">
                    <Controller
                        name="insurance_name"
                        control={control}
                        defaultValue=""
                        render={({ field }) => {
                            const insuranceOptions = filteredInsuranceListing.map(toListingOption);
                            const fieldValue = field.value as unknown as string | ListingOption;
                            const selectedInsurance = typeof fieldValue === "string"
                                ? insuranceOptions.find((opt) => opt.label === fieldValue || opt.value === fieldValue) ??
                                (fieldValue ? { value: fieldValue, label: fieldValue } : null)
                                : fieldValue || null;

                            const handleInputChange = (inputValue: string, actionMeta: any) => {
                                if (actionMeta.action === "input-change") {
                                    debouncedFilterInsurance(inputValue);
                                    field.onChange({ value: inputValue, label: inputValue });
                                }
                            };

                            return (
                                <Select<ListingOption, false>
                                    name={field.name}
                                    onBlur={field.onBlur}
                                    id="insurance_name"
                                    placeholder="Select Insurance"
                                    isSearchable
                                    isClearable
                                    options={insuranceOptions}
                                    value={selectedInsurance}
                                    onChange={(selected) => {
                                        field.onChange(selected?.label || "");
                                    }}
                                    onInputChange={handleInputChange}
                                    noOptionsMessage={({ inputValue }) => {
                                        if (!inputValue) return "Please enter 2 or more characters to search...";
                                        if (inputValue.length < 2)
                                            return "Please enter 2 or more characters to search...";
                                        return `No results found for "${inputValue}"`;
                                    }}
                                />
                            );
                        }}
                    />
                </div>)}
            {showPracticeCity && (
                <div className="col min-col mb-2">
                    <Controller
                        name="practice_city"
                        control={control}
                        render={({ field }) => (
                            <input
                                {...field}
                                type="text"
                                className="form-control"
                                placeholder="Practice City"
                            />
                        )}
                    />
                </div>
            )}
            {showHcpAddress && (
                <div className="col min-col mb-2">
                    <Controller
                        name="hcp_address"
                        control={control}
                        render={({ field }) => (
                            <input
                                {...field}
                                type="text"
                                className="form-control"
                                placeholder="HCP Address"
                            />
                        )}
                    />
                </div>
            )}
            {showCommunicationMethod && (
                <div className="col min-col mb-2">
                    <Controller
                        name="communication_method"
                        control={control}
                        render={({ field }) => (
                            <Select
                                {...field}
                                id="communication_method"
                                placeholder="Communication Method"
                                isSearchable={false}
                                isClearable={true}
                                options={COMMUNICATION_METHOD_OPTIONS}
                                value={
                                    COMMUNICATION_METHOD_OPTIONS.find(
                                        (opt) => opt.value === field.value
                                    ) || null
                                }
                                onChange={(selected) => {
                                    field.onChange(
                                        (selected as { value: string; label: string } | null)?.value || ""
                                    );
                                }}
                            />
                        )}
                    />
                </div>
            )}
            <div className="col btn-col mb-2">
                <div className="row g-2">
                    <div className="col-6">
                        <button type="submit" className="btn btn-primary w-100 rounded">Search</button>
                    </div>
                    <div className="col-6">
                        <button type="button" onClick={clearFormData} className="btn btn-outline-secondary w-100 rounded">Clear</button>
                    </div>
                </div>
            </div>
        </form>
    );
};

export default ReportsFilter;
